import { Injectable } from '@angular/core';
import { CoreProvider } from '../core.provider';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { CustomEliteConfigurationModelDto } from '../api/models';

export type EliteConfiguration = CustomEliteConfigurationModelDto & {
  localLastUpdate: number|null;
  pendingDelete: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EliteConfigurationService {
  private readonly ELITE_CONFIGURATION_STORAGE_KEY = 'elite-configurations';
  private core?: CoreProvider;

  private eliteConfigurationsSubject = new BehaviorSubject<EliteConfiguration[]>([]);
  public get eliteConfigurations$(): Observable<EliteConfiguration[]> {
    return this.eliteConfigurationsSubject.asObservable();
  }

  constructor() {}

  async initCore(core: CoreProvider) {
    this.core = core;
    this.getEliteConfigurations();
  }

  private generateLocalId(): string {
    return crypto.randomUUID();
  }

  async getEliteConfigurations(): Promise<EliteConfiguration[]> {
    const configurationsJson = await this.core!.storage.getItem(this.ELITE_CONFIGURATION_STORAGE_KEY);
    const configurations = configurationsJson ? (JSON.parse(configurationsJson) as EliteConfiguration[]).sort((a, b) => a.name.localeCompare(b.name)) : [];
    this.eliteConfigurationsSubject.next(configurations);
    return configurations;
  }

  async saveEliteConfiguration(configurationName: string): Promise<EliteConfiguration> {
    const configurations = await this.getEliteConfigurations();
    const newConfiguration: EliteConfiguration = {
      localId: this.generateLocalId(),
      localLastUpdate: Date.now(),
      pendingDelete: false,
      name: configurationName,
      buttons: []
    };
    configurations.push(newConfiguration);
    this.updateEliteConfigurations(configurations);
    return newConfiguration;
  }

  async updateEliteConfigurations(configurations: EliteConfiguration[], preventCloudUpdate: boolean = false): Promise<void> {
    await this.core!.storage.setItem(this.ELITE_CONFIGURATION_STORAGE_KEY, JSON.stringify(configurations));
    this.eliteConfigurationsSubject.next(configurations);
    if (!preventCloudUpdate) await this.syncToCloud();
  }

  async syncToCloud(): Promise<void> {
    const syncedConfigurations = await this.core!.storage.syncToCloud(await this.getEliteConfigurations(), {
      getall: () => firstValueFrom(this.core!.api.customEliteConfigurationService.customEliteConfigurationControllerGetConfigurations()),
      create: (configuration) => firstValueFrom(this.core!.api.customEliteConfigurationService.customEliteConfigurationControllerCreateConfiguration({ body: configuration })),
      delete: (configuration) => firstValueFrom(this.core!.api.customEliteConfigurationService.customEliteConfigurationControllerDeleteConfiguration({ uuid: configuration.uuid! })),
      update: (configuration) => firstValueFrom(this.core!.api.customEliteConfigurationService.customEliteConfigurationControllerUpdateConfiguration({ uuid: configuration.uuid!, body: configuration })),
    }, cloudConfiguration => ({
      ...cloudConfiguration,
      localLastUpdate: null,
      pendingDelete: false
    } as EliteConfiguration));
    // Extra check user selected custom elite configuration
    const user = await this.core!.storage.getUserProfile();
    if (user && user.selectedCustomEliteConfiguration && !syncedConfigurations.find(configuration => configuration.localId === user.selectedCustomEliteConfiguration)) {
      user.selectedCustomEliteConfiguration = undefined;
      await this.core!.storage.setUserProfile(user);
    }
    // Fire synced configurations to local data
    this.updateEliteConfigurations(syncedConfigurations, true);
  }

}
